import React, { useState } from "react";
import useTable from "../../../hooks/useTable";
import { GetSupplierPaymentHistoryParams } from "../../../types/statements";
import { Button, DatePicker, Dropdown, Input, Select, TimePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { API_URL, dbDateFormat } from "../../../constants/constants";
import { disableFutureDates } from "../../../lib/time";
import { ColumnsType } from "antd/es/table";
import AppLayout from "../../../layouts/AppLayout";
import IconButton from "../../../components/ui/IconButton";
import BottomDropdownIcon from "../../../assets/icons/BottomDropdownIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import CustomTable from "../../../components/ui/CustomTable";
import { addKeyInArray } from "../../../utils/utils";
import moment from "moment";
import { useGetTraderListQuery } from "../../../features/trader/traderApi";
import useDownloadEmployeeXlsx from "../../../hooks/useDownloadExcelWithApi";
import { BASE_API } from "../../../constants/apiUrls";
import {
  GetTraderListResponseType,
  TraderDetailsData,
} from "../../../types/trader.type";
import { removeHash } from "../../../utils/hash";
import emptyUser from "../../../assets/images/empty_user.jpg";

type TimeFormat = {
  formTime?: string;
  toTime?: string;
};

export default function TraderList() {
  const [time, setTime] = useState<TimeFormat>();
  const [openDropdown, setOpenDropdown] = useState(false);
  const { downloadXlsx, loading: xlLoaidng } = useDownloadEmployeeXlsx();
  const {
    page,
    limit,
    handlePagination,
    handleLimit,
    filter,
    handleFilter,
    handleSearch,
    keyword,
  } = useTable<GetSupplierPaymentHistoryParams>();

  const product = localStorage.getItem("product-type") || "";
  const pId = removeHash(JSON.parse(product));

  const { data: treaders, isLoading: loading } = useGetTraderListQuery({
    page,
    limit,
    search: keyword,
  });

  const handleStatementCancel = () => {
    handleFilter("startDate", undefined);
    handleFilter("endDate", undefined);
    setTime(undefined);
  };

  const handleDownload = () => {
    downloadXlsx(
      API_URL + BASE_API.getTraderList, // 🔗 API Endpoint
      {
        productId: pId,
        skip: 0,
        limit: 100000000000,
      },
      `sPay Statement_treaders_list${moment().format(
        "DD MMMM YYYY"
      )}_${moment().format("h.mm.ss A")}`,
      "rows",
      (item: TraderDetailsData) => ({
        "Dealer Code": item?.suppliers?.dealerCode || "-",
        "Dealer Name": item?.suppliers?.name || "-",
        "Trader Number": item?.traderMsisdn || "N/A",
        "Trader Name": item?.traderTitle || "-",
        Zone: item?.suppliers?.zone || "N/A",
        District: item?.suppliers?.district || "N/A",
        "Onboard Date": item?.createdAt
          ? moment(item?.createdAt).format("DD/MM/YYYY")
          : "-",
      })
    );
  };

  const DateTimeSelector = (
    <div className="p-6 border rounded-lg shadow-md max-w-md mx-auto bg-white clear-button-hide">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-gray-600 mb-1">From Date</label>
          <DatePicker
            value={
              filter?.startDate ? dayjs(filter?.startDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) =>
              handleFilter("startDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.formTime ? dayjs(time?.formTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) =>
              setTime((prev) => ({
                ...prev,
                formTime: dayjs(e).format("HH:mm:ss"),
              }))
            }
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">To Date</label>
          <DatePicker
            value={
              filter?.endDate ? dayjs(filter?.endDate, dbDateFormat) : null
            }
            onChange={(e: Dayjs | null) =>
              handleFilter("endDate", dayjs(e).format(dbDateFormat))
            }
            className="w-full"
            size="large"
            disabledDate={disableFutureDates}
          />
        </div>
        <div>
          <label className="block text-gray-600 mb-1">Time</label>
          <TimePicker
            value={time?.toTime ? dayjs(time?.toTime, "HH:mm:ss") : null}
            onChange={(e: Dayjs | null) => {
              setTime((prev) => ({
                ...prev,
                toTime: dayjs(e).format("HH:mm:ss"),
              }));
            }}
            className="w-full"
            format="hh:mm A"
            size="large"
            use12Hours
          />
        </div>
      </div>
      <div className="flex justify-end mt-6">
        <Button className="text-gray-600" onClick={handleStatementCancel}>
          Cancel
        </Button>
        {/* <Button
                  type="primary"
                  className="!text-primary !bg-[#147AFF33]"
                  onClick={() => setOpenDropdown(false)}
                >
                  View Statement
                </Button> */}
      </div>
    </div>
  );

  const columns: ColumnsType<GetTraderListResponseType> = [
    {
      title: <div className="text-nowrap">Dealer Code</div>,
      dataIndex: "suppliers",
      align: "center",
      render: (val) => {
        return val?.dealerCode || "-";
      },
    },
    {
      title: <div style={{ width: "200px" }}>Dealer Name</div>,
      dataIndex: "suppliers",
      render: (val) => {
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full">
              <img
                src={emptyUser}
                alt="Dealer"
                className="rounded-full w-full h-full object-cover"
              />
            </div>

            <span className="flex-1">{val?.name}</span>
          </div>
        );
      },
    },
    {
      title: "Trader Number",
      dataIndex: "profile",
      align: "center",
      render: (val) => {
        return val ? val?.merchantNumber : "N/A";
      },
    },
    {
      title: <div style={{ width: "200px" }}>Trader Name</div>,
      dataIndex: "profile",
      render: (val) => {
        return (
          <div className="flex items-center gap-2">
            <div className="w-12 h-12 rounded-full">
              <img
                src={val?.logoImage || emptyUser}
                alt="Dealer"
                className="rounded-full w-full h-full object-cover"
              />
            </div>

            <span className="flex-1">{val?.merchantName}</span>
          </div>
        );
      },
    },
    {
      title: "Zone",
      dataIndex: "suppliers",
      align: "center",
      render: (val) => {
        return val ? val?.zone : "N/A";
      },
    },
    {
      title: "District",
      dataIndex: "suppliers",
      align: "center",
      render: (val) => {
        return val ? val?.district : "N/A";
      },
    },

    {
      title: "Onboard Date",
      key: "date",
      align: "center",
      render: (val) => {
        return (
          <span className="text-nowrap">
            {val?.createdAt ? moment(val?.createdAt).format("DD/MM/YYYY") : "-"}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <AppLayout title="Trader List">
        <div className="self-stretch justify-start !text-[#2D3643] text-4xl font-medium mb-5">
          Trader List
        </div>
        <div className="flex justify-between items-center ">
          <div className="flex justify-start items-center gap-3">
            {/* <Dropdown
              overlay={DateTimeSelector}
              trigger={["click"]}
              open={openDropdown}
              onOpenChange={setOpenDropdown}
            >
              <IconButton
                className="flex items-center text-[#7a8799]"
                // nextIcon={<BottomDropdownIcon color="#7a8799" />}
              >
                Onboard Date
              </IconButton>
            </Dropdown>

            <Select
              placeholder="Zone"
              suffixIcon={<BottomDropdownIcon color="#7a8799" />}
              // onChange={(val) => handleFilter("spayStatus", val)}
              className="min-w-[100px]"
              allowClear
            />

            <Select
              placeholder="District"
              suffixIcon={<BottomDropdownIcon color="#7a8799" />}
              // onChange={(val) => handleFilter("spayStatus", val)}
              className="min-w-[100px]"
              allowClear
            /> */}
          </div>

          <div className="flex justify-start items-center gap-3">
            <Input
              placeholder="Search dealer’s name or code"
              className="text-[#AFBACA] !w-[280px]"
              prefix={<SearchIcon />}
              onChange={handleSearch}
              allowClear
            />
            <Button
              className="!text-primary bg-[#147AFF33]"
              loading={xlLoaidng}
              onClick={handleDownload}
            >
              Export
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <CustomTable
            columns={columns}
            className="text-header overflow-auto mt-4"
            loading={loading}
            handleLimit={handleLimit}
            handlePagination={handlePagination}
            limit={limit}
            dataList={addKeyInArray(treaders?.data?.rows)}
            page={page}
            bordered={false}
            total={treaders?.data?.count}
            hideSpace
            paginationClassnames="dark:bg-primaryBackground bg-white dark:border-others-body border-others-borderLight py-4 px-4 relative"
            // showTotalPage
          />
        </div>
      </AppLayout>
    </>
  );
}
